import { startTransition, useEffect, useReducer, useRef, useState } from "react";
import { trackCtaClicked, trackExperienceOpened } from "@/analytics/tracker";
import { demoControlReducer, initialDemoControls, writeExploreControlsToUrl } from "@/app/demoControlState";
import { ExperienceHud } from "@/app/ExperienceHud";
import { initialDemoFromSearch, replaceDemoInUrl } from "@/app/navigation";
import { installMobileConsole } from "@/debug/mobileConsole";
import { getDemo, nextDemoId, preloadDemo } from "@/demos/registry";
import type { DemoId } from "@/demos/types";
import { reportRuntimeError } from "@/runtime/runtimeErrors";
import { EighthWallSceneStage } from "@/scene/EighthWallSceneStage";
import { SceneStage } from "@/scene/SceneStage";
import { useDemoPreloading } from "@/app/useDemoPreloading";
import { useExperienceRuntime } from "@/app/useExperienceRuntime";
import { useTransientNotice } from "@/app/useTransientNotice";

const MEDIA_URL = `${import.meta.env.BASE_URL}assets/media/holographic-card.mp4`;
const POSTER_URL = `${import.meta.env.BASE_URL}assets/media/holographic-card-poster.svg`;

export function ExperienceShell() {
  const [activeDemoId, setActiveDemoId] = useState<DemoId>(() => initialDemoFromSearch(window.location.search));
  const [controls, dispatchControls] = useReducer(demoControlReducer, undefined, () => initialDemoControls());
  const [loadingNext, setLoadingNext] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { notice, showNotice } = useTransientNotice();
  const runtime = useExperienceRuntime({ activeDemoId, showNotice, videoRef });
  const activeDemo = getDemo(activeDemoId);

  useEffect(() => {
    void installMobileConsole().catch((cause) => {
      reportRuntimeError(cause, "Debug console failed to load");
    });
  }, []);

  useEffect(() => {
    trackExperienceOpened(activeDemoId);
  }, [activeDemoId]);

  useEffect(() => {
    if (activeDemoId === "explore") {
      writeExploreControlsToUrl({
        variant: controls.exploreVariant,
        inspection: controls.exploreInspection,
        animation: controls.exploreAnimation,
      });
    }
  }, [activeDemoId, controls.exploreAnimation, controls.exploreInspection, controls.exploreVariant]);

  useDemoPreloading({
    activeDemoId,
    started: runtime.started,
    phase: runtime.phase,
    eighthWallRuntime: runtime.eighthWallRuntime,
    getCapabilities: runtime.getCapabilities,
  });

  const nextDemo = async () => {
    if (loadingNext) return;
    const nextId = nextDemoId(activeDemoId);
    setLoadingNext(true);
    try {
      await preloadDemo(nextId);
      startTransition(() => {
        setActiveDemoId(nextId);
        replaceDemoInUrl(nextId);
      });
    } catch (cause) {
      reportRuntimeError(cause, "Next experience load failed");
      runtime.setError("The next experience could not be loaded. The current demo is still available.");
    } finally {
      setLoadingNext(false);
    }
  };

  const [reducedMotion] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  const stageProps = {
    placement: runtime.placement,
    onPlacement: runtime.handlePlacement,
    onPlacementProgress: runtime.setPlacementProgress,
    Scene: activeDemo.Scene,
    arFloorOffsetY: activeDemo.arFloorOffsetY,
    quality: runtime.quality,
    onQualityChange: runtime.setQuality,
    reducedMotion,
    resetEpoch: runtime.resetEpoch,
    mediaUrl: MEDIA_URL,
    posterUrl: POSTER_URL,
    controls,
    onAction: dispatchControls,
    onWebglContextLost: runtime.handleWebglContextLost,
  };

  return (
    <main className={`experience-shell mode-${runtime.mode} phase-${runtime.phase}`} data-testid="experience-shell">
      <div className="ambient-field" aria-hidden="true" />
      <div id="mobile-console-root" className="mobile-console-root" data-ui-control />
      <video ref={videoRef} className="camera-feed" muted playsInline aria-hidden="true" />
      {runtime.webglAvailable && runtime.mode === "eighth-wall" ? (
        <EighthWallSceneStage
          {...stageProps}
          runtime={runtime.eighthWallRuntime}
          onRuntimePhase={runtime.setPhase}
          onRuntimeError={runtime.handleEighthWallError}
        />
      ) : runtime.webglAvailable ? <SceneStage {...stageProps} mode={runtime.mode} /> : null}

      <ExperienceHud
        webglAvailable={runtime.webglAvailable}
        started={runtime.started}
        mode={runtime.mode}
        phase={runtime.phase}
        activeDemoId={activeDemoId}
        activeDemoTitle={activeDemo.title}
        placementProgress={runtime.placementProgress}
        error={runtime.error}
        notice={notice}
        loadingNext={loadingNext}
        runtimeError={runtime.runtimeError}
        controls={controls}
        onStart={runtime.startExperience}
        onVisit={() => trackCtaClicked(activeDemoId)}
        onRetry={runtime.startExperience}
        onContinue={runtime.continueIn3D}
        onReposition={runtime.reposition}
        onUseCamera={runtime.leaveWebXRForCamera}
        onNextDemo={nextDemo}
        onDismissRuntimeError={() => runtime.setRuntimeError(null)}
        onAction={dispatchControls}
      />
    </main>
  );
}
